import { reactive } from "vue";
import { ElMessage } from "element-plus";
import { defHttp, noTokenHttp } from "./http";

export const graphicCaptchaState = reactive({
  visible: false,
  title: "安全验证",
  description: "拖动下方滑块完成拼图",
  scene: "",
  bizId: "",
  deviceId: "",
  fingerprint: "",
  captchaId: "",
  bgImage: "",
  sliderImage: "",
  bgWidth: 320,
  bgHeight: 160,
  jigsawWidth: 52,
  jigsawHeight: 52,
  offsetY: 0,
  expireSeconds: 120,
  expiresAt: 0,
  remainTimes: null,
  verifyToken: "",
  authRequired: false,
  loading: false,
  verifying: false,
  errorMessage: "",
  challengeSeed: 0,
});

const DEVICE_ID_STORAGE_KEY = "graphic_captcha_device_id_v1";
const DEFAULT_BG_WIDTH = 320;
const DEFAULT_BG_HEIGHT = 160;
const DEFAULT_JIGSAW_SIZE = 52;

let pendingResolve = null;
let pendingReject = null;
let activeRequestId = 0;

function safeStorageGet(key) {
  try {
    return localStorage.getItem(key);
  } catch (error) {
    return "";
  }
}

function safeStorageSet(key, value) {
  try {
    localStorage.setItem(key, value);
  } catch (error) {
    return;
  }
}

function createRandomId() {
  return `${Date.now().toString(36)}${Math.random().toString(36).slice(2, 10)}`;
}

export function getStableDeviceId() {
  const cachedValue = safeStorageGet(DEVICE_ID_STORAGE_KEY);
  if (cachedValue) {
    return cachedValue;
  }

  const deviceId = `web-${createRandomId()}`;
  safeStorageSet(DEVICE_ID_STORAGE_KEY, deviceId);
  return deviceId;
}

function createHash(text = "") {
  let hash = 0;

  for (let index = 0; index < text.length; index += 1) {
    hash = (hash << 5) - hash + text.charCodeAt(index);
    hash |= 0;
  }

  return Math.abs(hash).toString(16);
}

export function getBrowserFingerprint() {
  const platform =
    navigator.userAgentData?.platform ||
    navigator.userAgentData?.brands?.map((item) => item.brand).join(",") ||
    "";
  const screenSize = window.screen
    ? `${window.screen.width}x${window.screen.height}x${window.screen.colorDepth}`
    : "0x0x0";

  return createHash(
    [
      navigator.userAgent,
      navigator.language,
      platform,
      screenSize,
      Intl.DateTimeFormat().resolvedOptions().timeZone || "",
    ].join("|"),
  );
}

function clearCaptchaPayload() {
  graphicCaptchaState.captchaId = "";
  graphicCaptchaState.bgImage = "";
  graphicCaptchaState.sliderImage = "";
  graphicCaptchaState.bgWidth = DEFAULT_BG_WIDTH;
  graphicCaptchaState.bgHeight = DEFAULT_BG_HEIGHT;
  graphicCaptchaState.jigsawWidth = DEFAULT_JIGSAW_SIZE;
  graphicCaptchaState.jigsawHeight = DEFAULT_JIGSAW_SIZE;
  graphicCaptchaState.offsetY = 0;
  graphicCaptchaState.expireSeconds = 120;
  graphicCaptchaState.expiresAt = 0;
  graphicCaptchaState.remainTimes = null;
  graphicCaptchaState.verifyToken = "";
  graphicCaptchaState.errorMessage = "";
}

function resetState() {
  clearCaptchaPayload();
  graphicCaptchaState.visible = false;
  graphicCaptchaState.title = "安全验证";
  graphicCaptchaState.description = "拖动下方滑块完成拼图";
  graphicCaptchaState.scene = "";
  graphicCaptchaState.bizId = "";
  graphicCaptchaState.deviceId = "";
  graphicCaptchaState.fingerprint = "";
  graphicCaptchaState.authRequired = false;
  graphicCaptchaState.loading = false;
  graphicCaptchaState.verifying = false;
  graphicCaptchaState.challengeSeed = 0;
}

function rejectPending(reason) {
  if (pendingReject) {
    pendingReject(new Error(reason));
  }
  pendingResolve = null;
  pendingReject = null;
}

function buildCaptchaRequestPayload() {
  const payload = {
    scene: graphicCaptchaState.scene,
    bizId: graphicCaptchaState.bizId || undefined,
    deviceId: graphicCaptchaState.deviceId || undefined,
    fingerprint: graphicCaptchaState.fingerprint || undefined,
  };

  return Object.fromEntries(
    Object.entries(payload).filter(([, value]) => value !== undefined && value !== ""),
  );
}

function applyCaptchaData(data = {}) {
  clearCaptchaPayload();
  graphicCaptchaState.captchaId = data.captchaId || "";
  graphicCaptchaState.bgImage = data.bgImage || "";
  graphicCaptchaState.sliderImage = data.sliderImage || "";
  graphicCaptchaState.bgWidth = Number(data.bgWidth) || DEFAULT_BG_WIDTH;
  graphicCaptchaState.bgHeight = Number(data.bgHeight) || DEFAULT_BG_HEIGHT;
  graphicCaptchaState.jigsawWidth =
    Number(data.jigsawWidth) || DEFAULT_JIGSAW_SIZE;
  graphicCaptchaState.jigsawHeight =
    Number(data.jigsawHeight) || DEFAULT_JIGSAW_SIZE;
  graphicCaptchaState.offsetY = Number(data.offsetY) || 0;
  graphicCaptchaState.expireSeconds = Number(data.expireSeconds) || 120;
  graphicCaptchaState.expiresAt =
    Date.now() + graphicCaptchaState.expireSeconds * 1000;
  graphicCaptchaState.challengeSeed = Date.now() + Math.random();
}

async function requestCaptcha(endpoint) {
  const requestId = Date.now() + Math.random();
  activeRequestId = requestId;
  graphicCaptchaState.loading = true;
  graphicCaptchaState.errorMessage = "";

  try {
    const request = graphicCaptchaState.authRequired ? defHttp : noTokenHttp;
    const response = await request.post(
      endpoint,
      buildCaptchaRequestPayload(),
      {
        skipGlobalErrorMessage: true,
      },
    );

    if (activeRequestId !== requestId) {
      return null;
    }

    const { data } = response || {};

    if (data?.code !== 200 || !data?.data?.captchaId) {
      throw new Error(data?.msg || "获取图形验证码失败");
    }

    applyCaptchaData(data.data);
    return data.data;
  } catch (error) {
    const message =
      error?.response?.data?.msg ||
      error?.message ||
      "获取图形验证码失败，请稍后重试";
    graphicCaptchaState.errorMessage = message;
    throw new Error(message);
  } finally {
    if (activeRequestId === requestId) {
      graphicCaptchaState.loading = false;
    }
  }
}

export function openGraphicCaptcha(options = {}) {
  if (graphicCaptchaState.visible && pendingReject) {
    rejectPending("graphic captcha replaced");
  }

  graphicCaptchaState.title = options.title || "安全验证";
  graphicCaptchaState.description =
    options.description || "拖动下方滑块完成拼图";
  graphicCaptchaState.scene = options.scene || "";
  graphicCaptchaState.bizId = options.bizId || "";
  graphicCaptchaState.deviceId = options.deviceId || getStableDeviceId();
  graphicCaptchaState.fingerprint =
    options.fingerprint || getBrowserFingerprint();
  graphicCaptchaState.authRequired = options.authRequired === true;
  clearCaptchaPayload();
  graphicCaptchaState.challengeSeed = Date.now();
  graphicCaptchaState.visible = true;

  const promise = new Promise((resolve, reject) => {
    pendingResolve = resolve;
    pendingReject = reject;
  });

  requestCaptcha("/api/user-api/captcha/puzzle/create").catch((error) => {
    ElMessage.error(error.message || "获取图形验证码失败");
    closeGraphicCaptcha(error.message || "graphic captcha init failed");
  });

  return promise;
}

export async function refreshGraphicCaptcha() {
  graphicCaptchaState.verifyToken = "";
  graphicCaptchaState.remainTimes = null;

  try {
    return await requestCaptcha("/api/user-api/captcha/puzzle/refresh");
  } catch (error) {
    ElMessage.error(error.message || "刷新图形验证码失败");
    return null;
  }
}

export async function verifyGraphicCaptcha(payload = {}) {
  if (!graphicCaptchaState.captchaId) {
    throw new Error("图形验证码不存在，请刷新后重试");
  }

  graphicCaptchaState.verifying = true;
  graphicCaptchaState.errorMessage = "";

  try {
    const request = graphicCaptchaState.authRequired ? defHttp : noTokenHttp;
    const response = await request.post(
      "/api/user-api/captcha/puzzle/verify",
      {
        captchaId: graphicCaptchaState.captchaId,
        ...payload,
      },
      {
        skipGlobalErrorMessage: true,
      },
    );

    const { data } = response || {};

    if (data?.code === 200) {
      const result = data.data || {};
      graphicCaptchaState.remainTimes =
        typeof result.remainTimes === "number" ? result.remainTimes : null;

      if (result.passed) {
        graphicCaptchaState.verifyToken = result.verifyToken || "";
        resolveGraphicCaptcha({
          passed: true,
          verifyToken: result.verifyToken || "",
          needRefresh: false,
          remainTimes: result.remainTimes,
          captchaId: graphicCaptchaState.captchaId,
          scene: graphicCaptchaState.scene,
        });
        return {
          status: "passed",
          data: result,
        };
      }

      if (result.needRefresh) {
        ElMessage.warning("验证失败，请刷新后重试");
        await refreshGraphicCaptcha();
        return {
          status: "refresh",
          data: result,
        };
      }

      ElMessage.error("验证失败，请重试");
      return {
        status: "retry",
        data: result,
      };
    }

    if (data?.code === 400) {
      ElMessage.warning(data.msg || "图形验证码已失效，请刷新后重试");
      await refreshGraphicCaptcha();
      return {
        status: "expired",
        data: null,
      };
    }

    throw new Error(data?.msg || "图形验证码校验失败");
  } finally {
    graphicCaptchaState.verifying = false;
  }
}

export function resolveGraphicCaptcha(payload = {}) {
  const resolve = pendingResolve;
  pendingResolve = null;
  pendingReject = null;
  resetState();
  if (resolve) {
    resolve(payload);
  }
}

export function closeGraphicCaptcha(reason = "graphic captcha closed") {
  rejectPending(reason);
  resetState();
}
